/**
 * parseParkedPackageStatement — lightweight lexical scan that pulls Oracle
 * package shell fields out of the parked statement text so the inline
 * `[Edit Package]` popover can seed its form. Oracle-only.
 *
 * Accepts CREATE / ALTER / DROP PACKAGE [BODY] variants.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7 — Procedure / Function shell UI
 * (oracle packages variant).
 */
import type { QualifiedName } from '../../../sql-grammar/model';
import type { PackageKindShell } from '../../../sql-resolver/types/ddlEdit';

const IDENT = '[A-Za-z_][A-Za-z0-9_]*|"[^"]+"|`[^`]+`|\\[[^\\]]+\\]';
const QUALIFIED_NAME_RE = new RegExp(`^(${IDENT})(?:\\s*\\.\\s*(${IDENT}))?`);

export interface ParsedParkedPackage {
  readonly action: 'create' | 'alter' | 'drop';
  readonly kind: PackageKindShell;
  readonly name: QualifiedName;
  readonly bodyText?: string;
  readonly orReplace?: boolean;
  readonly oracleEditionable?: boolean;
  readonly oracleNonEditionable?: boolean;
}

function unquoteIdent(raw: string): { name: string; quoted: boolean } {
  if (raw.length >= 2 && raw.charAt(0) === '"' && raw.charAt(raw.length - 1) === '"') {
    return { name: raw.slice(1, -1), quoted: true };
  }
  return { name: raw, quoted: false };
}

function buildQualifiedName(rawHead: string, rawTail: string | undefined): QualifiedName {
  if (rawTail !== undefined) {
    const head = unquoteIdent(rawHead);
    const tail = unquoteIdent(rawTail);
    return {
      schema: head.name, name: tail.name,
      raw: `${rawHead}.${rawTail}`, quoted: head.quoted || tail.quoted,
      foldedName: tail.name.toLowerCase(),
      foldedSchema: head.name.toLowerCase(),
    };
  }
  const ident = unquoteIdent(rawHead);
  return {
    name: ident.name, raw: rawHead, quoted: ident.quoted,
    foldedName: ident.name.toLowerCase(),
  };
}

function skipSpaces(s: string, start: number): number {
  let i = start;
  while (i < s.length && /\s/.test(s.charAt(i))) i++;
  return i;
}

function consumeKeyword(s: string, start: number, keyword: string): number {
  const upper = s.slice(start, start + keyword.length).toUpperCase();
  if (upper !== keyword) return -1;
  const next = s.charAt(start + keyword.length);
  if (next && /[A-Za-z0-9_]/.test(next)) return -1;
  return start + keyword.length;
}

export function parseParkedPackageStatement(stmtText: string): ParsedParkedPackage | null {
  const trimmed = stmtText.trim();
  const upper = trimmed.toUpperCase();

  let action: 'create' | 'alter' | 'drop';
  let cursor: number;
  if (upper.startsWith('CREATE')) { action = 'create'; cursor = 'CREATE'.length; }
  else if (upper.startsWith('ALTER')) { action = 'alter'; cursor = 'ALTER'.length; }
  else if (upper.startsWith('DROP')) { action = 'drop'; cursor = 'DROP'.length; }
  else return null;

  let orReplace = false;
  let oracleEditionable = false;
  let oracleNonEditionable = false;
  if (action === 'create') {
    cursor = skipSpaces(trimmed, cursor);
    const orRep = /^OR\s+REPLACE\b/i.exec(trimmed.slice(cursor));
    if (orRep) { orReplace = true; cursor += orRep[0].length; }
    cursor = skipSpaces(trimmed, cursor);
    const ed = consumeKeyword(trimmed, cursor, 'EDITIONABLE');
    if (ed !== -1) { oracleEditionable = true; cursor = ed; }
    const ned = consumeKeyword(trimmed, cursor, 'NONEDITIONABLE');
    if (ned !== -1) { oracleNonEditionable = true; cursor = ned; }
  }
  cursor = skipSpaces(trimmed, cursor);

  if (consumeKeyword(trimmed, cursor, 'PACKAGE') === -1) return null;
  cursor = consumeKeyword(trimmed, cursor, 'PACKAGE')!;
  cursor = skipSpaces(trimmed, cursor);

  let kind: PackageKindShell = 'spec';
  if (consumeKeyword(trimmed, cursor, 'BODY') !== -1) {
    kind = 'body'; cursor = consumeKeyword(trimmed, cursor, 'BODY')!;
    cursor = skipSpaces(trimmed, cursor);
  }

  const nameMatch = QUALIFIED_NAME_RE.exec(trimmed.slice(cursor));
  if (!nameMatch) return null;
  const name = buildQualifiedName(nameMatch[1]!, nameMatch[2]);
  cursor += nameMatch[0].length;

  if (action !== 'create') {
    return {
      action, kind, name,
      ...(orReplace ? { orReplace: true } : {}),
    };
  }

  // Body extraction — text after the first AS / IS keyword.
  const tail = trimmed.slice(cursor);
  const asMatch = /^\s+(?:AS|IS)\s+([\s\S]*)$/i.exec(tail);
  const bodyText = asMatch ? (asMatch[1] ?? '').trim() : undefined;

  return {
    action: 'create',
    kind,
    name,
    ...(bodyText ? { bodyText } : {}),
    ...(orReplace ? { orReplace: true } : {}),
    ...(oracleEditionable ? { oracleEditionable: true } : {}),
    ...(oracleNonEditionable ? { oracleNonEditionable: true } : {}),
  };
}
